'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';

import { requestAdminOtp, verifyAdminOtp } from './actions';
import { AdminAuthStage } from './admin-auth-stage';
import { AdminOtpCard } from './admin-otp-card';

import { BrandIcon } from '@/components/brand-icon';

type Step = 'email' | 'otp';

export function AdminLoginForm({ initialError = null }: { initialError?: string | null }) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(initialError);
  const [resendIn, setResendIn] = useState(0);
  const [pending, startTransition] = useTransition();
  const router = useRouter();

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  function handleRequest(formData: FormData) {
    setError(null);
    startTransition(async () => {
      const res = await requestAdminOtp(formData);
      if (!res.ok) {
        setError(res.error ?? 'Falha ao enviar código.');
        return;
      }
      setEmail((formData.get('email') as string | null) ?? '');
      setOtp('');
      setResendIn(45);
      setStep('otp');
    });
  }

  function verifyCode(code: string) {
    setError(null);
    startTransition(async () => {
      const fd = new FormData();
      fd.set('email', email);
      fd.set('token', code);
      const res = await verifyAdminOtp(fd);
      if (!res.ok) {
        setError(res.error ?? 'Código inválido.');
        setOtp('');
        return;
      }
      if (res.redirectTo) {
        router.push(res.redirectTo as never);
        router.refresh();
      }
    });
  }

  function resend() {
    if (resendIn > 0) return;
    setError(null);
    const fd = new FormData();
    fd.set('email', email);
    startTransition(async () => {
      const res = await requestAdminOtp(fd);
      if (!res.ok) {
        setError(res.error ?? 'Erro ao reenviar.');
        return;
      }
      setResendIn(45);
    });
  }

  return (
    <AdminAuthStage>
      {step === 'otp' ? (
        <AdminOtpCard
          email={email}
          value={otp}
          onChange={setOtp}
          onSubmit={verifyCode}
          onResend={resend}
          onBack={() => {
            setStep('email');
            setOtp('');
            setError(null);
          }}
          resendIn={resendIn}
          loading={pending}
          error={error}
        />
      ) : (
        <div className="card">
          <div className="inner">
            <BrandIcon size={56} className="logo stg d1" alt="Trakd" />
            <div className="badge stg d2">
              <span className="dot" />
              acesso restrito
            </div>
            <h1 className="brand stg d2">
              <span className="ink">Trakd</span> <span className="adm">Admin</span>
            </h1>
            <p className="sub stg d3">Painel interno · acesso restrito ao time.</p>

            <form action={handleRequest} className="field-wrap stg d4">
              <label className="mlabel" htmlFor="email">
                E-mail do admin
              </label>
              <div className="field">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  placeholder="voce@trakd.pro"
                />
              </div>

              {error && (
                <p className="err" style={{ marginTop: 12 }}>
                  {error}
                </p>
              )}

              <button type="submit" className="btn" disabled={pending} style={{ marginTop: 14 }}>
                {pending ? 'Enviando…' : 'Receber código'}
              </button>

              <p className="hint" style={{ marginTop: 12 }}>
                código de 6 dígitos · enviado pro seu e-mail
              </p>
            </form>
          </div>
        </div>
      )}
    </AdminAuthStage>
  );
}
